import { useState } from 'react'
import { supabase } from '../supabase'

const LOST_STAGES = [
  'Not shortlisted', 'Shortlisted but not selected', 'BAFO stage',
  'Final presentation', 'Preferred bidder overturned', 'Withdrawn by us'
]

const LOST_REASONS = [
  'Price / Commercial', 'Technical solution', 'Incumbent retained',
  'Buyer relationship', 'Insufficient track record', 'Resource / capacity concerns',
  'Out of scope', 'No bid decision', 'Other'
]

function WonModal({ opportunity, competitors, onSave, onClose }) {
  const [form, setForm] = useState({
    contract_value: opportunity.contract_value_max || opportunity.contract_value_min || '',
    start_date: '',
    duration_months: '',
    technical_score: '',
    commercial_score: '',
    win_factors: '',
    lessons_learned: '',
  })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    const { error } = await supabase
      .from('opportunities')
      .update({
        stage: 'won',
        outcome_detail: {
          type: 'won',
          contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
          start_date: form.start_date || null,
          duration_months: form.duration_months ? parseInt(form.duration_months) : null,
          technical_score: form.technical_score ? parseFloat(form.technical_score) : null,
          commercial_score: form.commercial_score ? parseFloat(form.commercial_score) : null,
          win_factors: form.win_factors || null,
          lessons_learned: form.lessons_learned || null,
          recorded_at: new Date().toISOString(),
        }
      })
      .eq('id', opportunity.id)
    setSaving(false)
    if (!error) onSave()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: 'var(--white)', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ background: 'var(--green-dark)', padding: '20px 24px' }}>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '4px' }}>Close Out — Won</div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.375rem', fontWeight: 600, color: 'var(--white)' }}>{opportunity.title}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '2px' }}>{opportunity.buyer}</div>
        </div>

        <div style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Confirmed Contract Value (£)</label>
              <input type="number" value={form.contract_value} onChange={e => setForm(p => ({ ...p, contract_value: e.target.value }))} placeholder="e.g. 2500000" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Contract Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Contract Duration (months)</label>
              <input type="number" value={form.duration_months} onChange={e => setForm(p => ({ ...p, duration_months: e.target.value }))} placeholder="e.g. 36" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Technical Score (%)</label>
                <input type="number" value={form.technical_score} onChange={e => setForm(p => ({ ...p, technical_score: e.target.value }))} placeholder="e.g. 85" min="0" max="100" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Commercial Score (%)</label>
                <input type="number" value={form.commercial_score} onChange={e => setForm(p => ({ ...p, commercial_score: e.target.value }))} placeholder="e.g. 72" min="0" max="100" />
              </div>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '16px' }}>
            <label>Key Win Factors — what made the difference?</label>
            <textarea value={form.win_factors} onChange={e => setForm(p => ({ ...p, win_factors: e.target.value }))} rows={3} style={{ width: '100%', resize: 'vertical' }} placeholder="e.g. Strong incumbent relationship, differentiated technical approach, competitive pricing on Year 1" />
          </div>

          <div className="form-group">
            <label>Lessons Learned (optional)</label>
            <textarea value={form.lessons_learned} onChange={e => setForm(p => ({ ...p, lessons_learned: e.target.value }))} rows={2} style={{ width: '100%', resize: 'vertical' }} placeholder="What would you do differently next time?" />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button className="btn btn--primary" onClick={handleSave} disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Saving...' : '🏆 Confirm Win'}
            </button>
            <button className="btn btn--secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function LostModal({ opportunity, competitors, onSave, onClose }) {
  const [form, setForm] = useState({
    lost_to: '',
    lost_stage: '',
    primary_reason: '',
    secondary_reason: '',
    technical_score: '',
    commercial_score: '',
    bid_again: '',
    lessons_learned: '',
  })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    const { error } = await supabase
      .from('opportunities')
      .update({
        stage: 'lost',
        outcome_detail: {
          type: 'lost',
          lost_to: form.lost_to || null,
          lost_stage: form.lost_stage || null,
          primary_reason: form.primary_reason || null,
          secondary_reason: form.secondary_reason || null,
          technical_score: form.technical_score ? parseFloat(form.technical_score) : null,
          commercial_score: form.commercial_score ? parseFloat(form.commercial_score) : null,
          bid_again: form.bid_again || null,
          lessons_learned: form.lessons_learned || null,
          recorded_at: new Date().toISOString(),
        }
      })
      .eq('id', opportunity.id)
    setSaving(false)
    if (!error) onSave()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: 'var(--white)', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ background: 'var(--charcoal)', padding: '20px 24px' }}>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--silver)', marginBottom: '4px' }}>Close Out — Lost</div>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.375rem', fontWeight: 600, color: 'var(--white)' }}>{opportunity.title}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '2px' }}>{opportunity.buyer}</div>
        </div>

        <div style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Lost To (competitor name)</label>
              <input
                type="text"
                value={form.lost_to}
                onChange={e => setForm(p => ({ ...p, lost_to: e.target.value }))}
                placeholder="e.g. Capita, Unknown"
                list="competitors-list"
              />
              <datalist id="competitors-list">
                {(competitors || []).map(c => <option key={c.id} value={c.name} />)}
              </datalist>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Lost at Stage</label>
              <select value={form.lost_stage} onChange={e => setForm(p => ({ ...p, lost_stage: e.target.value }))}>
                <option value="">Select stage</option>
                {LOST_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Primary Reason</label>
              <select value={form.primary_reason} onChange={e => setForm(p => ({ ...p, primary_reason: e.target.value }))}>
                <option value="">Select reason</option>
                {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Secondary Reason (optional)</label>
              <select value={form.secondary_reason} onChange={e => setForm(p => ({ ...p, secondary_reason: e.target.value }))}>
                <option value="">None</option>
                {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Technical Score (%)</label>
                <input type="number" value={form.technical_score} onChange={e => setForm(p => ({ ...p, technical_score: e.target.value }))} placeholder="e.g. 78" min="0" max="100" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Commercial Score (%)</label>
                <input type="number" value={form.commercial_score} onChange={e => setForm(p => ({ ...p, commercial_score: e.target.value }))} placeholder="e.g. 65" min="0" max="100" />
              </div>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Would you bid again?</label>
              <select value={form.bid_again} onChange={e => setForm(p => ({ ...p, bid_again: e.target.value }))}>
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="Yes — with changes">Yes — with changes</option>
                <option value="Maybe">Maybe</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '16px' }}>
            <label>Lessons Learned (optional)</label>
            <textarea value={form.lessons_learned} onChange={e => setForm(p => ({ ...p, lessons_learned: e.target.value }))} rows={3} style={{ width: '100%', resize: 'vertical' }} placeholder="What would you do differently? What did you learn about this buyer or competitor?" />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button className="btn btn--primary" onClick={handleSave} disabled={saving} style={{ flex: 1, background: 'var(--charcoal)', borderColor: 'var(--charcoal)' }}>
              {saving ? 'Saving...' : 'Confirm Loss'}
            </button>
            <button className="btn btn--secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { WonModal, LostModal }
